import { useRetainedState } from "@appretio/shared";
import type { ColumnState, FilterModel } from "ag-grid-enterprise";
import type { AgGridReact } from "ag-grid-react";
import {
  type Dispatch,
  type RefObject,
  type SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react";
import { calendarViewOptions } from "../components/tableControls/forecastTableControlUtils";
import type {
  ForecastTableControlOptions,
  ForecastTableControlValues
} from "../components/tableControls/forecastTableControlValues";
import { type CubePage, pageScope } from "../hooks/useCubeSelectionState";
import type { ForecastMetaDataType } from "../hooks/useFetchForecastMetaData";
import type { GridStateRetention } from "../hooks/useForecastWorkspace/useGridStateRetention";
import {
  BUILT_IN_VIEWS,
  type BuiltInViewKey,
  builtInViewKeysFor,
  DEFAULT_BUILT_IN_VIEW
} from "./builtInViews";
import { captureViewPayload } from "./captureViewPayload";
import { mergeDataShape } from "./dataShape";
import { resolveViewRows, selectedMetrics } from "./resolveViewRows";
import {
  ALL_VIEW_SECTIONS,
  DEFAULT_VIEW_ROWS,
  parseSavedViewPayload,
  type SavedViewPayload,
  sectionsOf,
  type ViewRows,
  type ViewSections
} from "./savedViewPayload";
import { useSavedViewList } from "./useSavedViewList";
import { isViewDirty } from "./viewDirty";

/** Inputs the hook needs to drive the bar and the grid. */
export interface UseViewStateParams {
  /**
   * Which grid's views to read and write. Doubles as the page's
   * retained-state scope: forecast and budget each remember their own
   * applied view.
   */
  grid: Extract<CubePage, "forecast" | "budget">;
  /**
   * Keeps the applied view to this mount instead of the page's scope, for a
   * grid inside another surface (the item modal): it lists, opens on and
   * writes the page's views, and applying one leaves the page's own alone.
   */
  transient?: boolean;
  /**
   * The built-in every opening lands, in place of the user's default. The
   * item modal opens on ``Compact Units`` whatever the user's page default.
   */
  openOn?: BuiltInViewKey;
  /** The grid's retention handle, told when a view lands. A transient grid has none. */
  gridStateRetention?: Pick<GridStateRetention, "viewApplied">;
  /** Live control values. */
  tableControls: ForecastTableControlValues;
  /** Control-value setter. */
  setTableControls: Dispatch<SetStateAction<ForecastTableControlValues>>;
  /** Live control options (period + planning-year lists follow the calendar). */
  tableControlOptions: ForecastTableControlOptions;
  /** Control-options setter. */
  setTableControlOptions: Dispatch<SetStateAction<ForecastTableControlOptions>>;
  /** Metric + year mappings; undefined until metadata lands. */
  metaData: ForecastMetaDataType | undefined;
  /** The grid, for capturing the layout section and the metric filter. */
  gridRef: RefObject<AgGridReact | null>;
  /**
   * What the opening waits for besides the metadata (and, for a default
   * opening, the list; an ``openOn`` opening does not wait for it). Defaults
   * to true.
   */
  readyToOpen?: boolean;
}

/** What a payload lands on the page, and the one path that lands it. */
function useApplyPayload({
  grid,
  transient,
  gridStateRetention,
  setTableControls,
  setTableControlOptions,
  metaData
}: Omit<
  UseViewStateParams,
  "gridRef" | "tableControls" | "tableControlOptions"
>) {
  // Retained per page, like the controls: navigating away and back finds
  // the same view applied, drift included.
  const scope = transient ? undefined : pageScope(grid);
  const [rows, setRows] = useRetainedState<ViewRows>(
    scope,
    "viewRows",
    DEFAULT_VIEW_ROWS
  );
  const [extraFilterModel, setExtraFilterModel] =
    useRetainedState<FilterModel | null>(scope, "viewExtraFilterModel", null);
  // Retained until the grid drains it: a view applied on the empty state
  // may not meet a grid before the planner leaves the page.
  const [pendingColumnState, setPendingColumnState] = useRetainedState<
    ColumnState[] | null
  >(scope, "viewPendingColumnState", null);
  // Counts the applies, so the grid can tell a new view from the same view
  // re-resolved for another calendar family (``ResolvedViewState.applyId``).
  const [applyId, setApplyId] = useState(0);

  // Order is fixed: controls first (the calendar family renames the year
  // column, so anything keyed on a colId has to follow), then the layout,
  // then the row selection. A view never touches what the grid shows *of*:
  // context has its own picker, and the cubes are the planner's per session.
  //
  // Functional updaters, not the render's values: on page open the
  // planning-year seed (``useWorkspaceTableState``) and the default view
  // land on the same metadata, in the same commit, and a plain write from
  // this closure would overwrite the seed.
  const applyPayload = useCallback(
    (payload: SavedViewPayload) => {
      setTableControls((prev) =>
        mergeDataShape(payload, { controls: prev, metaData })
      );
      const calendarView = payload.dataShape?.calendarView;
      if (calendarView) {
        setTableControlOptions((prev) => ({
          ...prev,
          ...calendarViewOptions(calendarView, metaData)
        }));
      }
      if (payload.rows) setRows(payload.rows);
      setExtraFilterModel(payload.layout?.filterModel ?? null);
      setPendingColumnState(payload.layout?.columnState ?? null);
      setApplyId((id) => id + 1);
      gridStateRetention?.viewApplied();
    },
    [
      metaData,
      gridStateRetention,
      setTableControls,
      setTableControlOptions,
      setRows,
      setExtraFilterModel,
      setPendingColumnState
    ]
  );

  return {
    rows,
    extraFilterModel,
    /**
     * Column layout the applied view is waiting to replay. The grid drains
     * it: a view can be applied before the grid mounts (a default view on
     * page open), and a period or calendar switch rebuilds the columns it
     * names.
     */
    pendingColumnState,
    clearPendingColumnState: useCallback(
      () => setPendingColumnState(null),
      [setPendingColumnState]
    ),
    applyId,
    applyPayload
  };
}

/**
 * The Views layer: which view is applied, whether the bar has drifted from
 * it, the grid state it resolves to, and the user's own views for the grid.
 */
export function useViewState(params: UseViewStateParams) {
  const { grid, tableControls, tableControlOptions, metaData, gridRef } =
    params;
  const { transient, openOn } = params;
  const { applyId, applyPayload, ...applied } = useApplyPayload(params);
  const { rows, extraFilterModel } = applied;

  const capture = useCallback(
    (sections: ViewSections) =>
      JSON.stringify(
        captureViewPayload(sections, {
          tableControls,
          rows,
          resolvedMetrics: selectedMetrics(
            rows,
            metaData?.metricCategoryMapping
          ),
          api: gridRef.current?.api ?? null
        })
      ),
    [tableControls, rows, metaData, gridRef]
  );

  // Waits for the metadata too: the year options a view's planning year is
  // checked against come from it.
  const list = useSavedViewList({
    page: grid,
    transient,
    openOn,
    builtIns: BUILT_IN_VIEWS,
    defaultBuiltIn: DEFAULT_BUILT_IN_VIEW,
    parse: parseSavedViewPayload,
    applyPayload,
    readyToOpen: metaData !== undefined && (params.readyToOpen ?? true),
    capture,
    sectionsOf,
    allSections: ALL_VIEW_SECTIONS
  });

  const resolvedViewState = useMemo(
    () => ({
      ...resolveViewRows(rows, {
        metaData,
        calendarView: tableControls.calendarView,
        extraFilterModel
      }),
      applyId
    }),
    [rows, metaData, tableControls.calendarView, extraFilterModel, applyId]
  );

  return {
    ...list,
    ...applied,
    isDirty: list.activePayload
      ? isViewDirty(list.activePayload, {
          controls: tableControls,
          options: tableControlOptions,
          rows
        })
      : false,
    resolvedViewState,
    /** The built-ins the picker offers for this grid. */
    builtInKeys: builtInViewKeysFor(grid)
  };
}

/** The Views layer, as the page and its controls consume it. */
export type ViewState = ReturnType<typeof useViewState>;
